import React, { useCallback, useEffect, useRef, useState } from "react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { formatJsonString, validateJsonObject } from "../websocket-config/utils"

interface JsonFieldProps {
  id: string
  label: string
  ariaLabel?: string
  placeholder: string
  description: string
  value: string | object | null | undefined
  fieldName: string
  onMetadataUpdate: (update: Record<string, unknown>) => void
  disabled: boolean
  rows?: number
  /** Optional context shown in an info tooltip next to the label. */
  tooltip?: React.ReactNode
}

export const JsonField: React.FC<JsonFieldProps> = ({
  id,
  label,
  ariaLabel,
  placeholder,
  description,
  value,
  fieldName,
  onMetadataUpdate,
  disabled,
  rows = 4,
  tooltip,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState(() => formatJsonString(value))
  const isEditingRef = useRef(false)

  useEffect(() => {
    if (!isEditingRef.current) {
      const formatted = formatJsonString(value)
      setText(formatted)
      setError(formatted.trim() === "" ? null : validateJsonObject(formatted))
    }
  }, [value])

  const handleChange = useCallback(
    (raw: string) => {
      setText(raw)
      setError(raw.trim() === "" ? null : validateJsonObject(raw))
      onMetadataUpdate({ [fieldName]: raw })
    },
    [fieldName, onMetadataUpdate],
  )

  const handleFocus = useCallback(() => {
    isEditingRef.current = true
  }, [])

  const handleBlur = useCallback(() => {
    isEditingRef.current = false
    const formatted = formatJsonString(text)
    setText(formatted)
    if (formatted !== text) {
      onMetadataUpdate({ [fieldName]: formatted })
    }
  }, [text, fieldName, onMetadataUpdate])

  return (
    <div className="space-y-2">
      <FieldLabel id={id} tooltip={tooltip ?? description}>
        {label}
      </FieldLabel>
      <JsonCodeEditor
        hideGutters
        copyable
        ariaLabel={ariaLabel || label}
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={rows}
        disabled={disabled}
        error={error}
      />
    </div>
  )
}
